import React from 'react';
import { Target, Eye, ThumbsUp, DollarSign } from 'lucide-react';

/**
 * Компонент для настройки целей рекламной кампании
 */
const GoalsSection = ({ 
  formData, 
  handleGoalsChange, 
  errors 
}) => {
  // Расчет суммы весов целей
  const totalWeight = parseFloat(formData.goals.awarenessWeight || 0) + 
                      parseFloat(formData.goals.engagementWeight || 0) + 
                      parseFloat(formData.goals.conversionWeight || 0);

  return (
    <div className="mb-6 bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg border border-teal-200 dark:border-teal-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Target className="w-5 h-5 mr-2 text-teal-500" />
        Цели кампании
      </h3>
      
      {errors.goals_total && (
        <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
          {errors.goals_total} (текущая сумма: {totalWeight.toFixed(1)}%)
        </div>
      )}

      <div className="space-y-4">
        {/* Осведомленность */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <label htmlFor="awarenessWeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <Eye className="w-4 h-4 mr-1 text-teal-500" />
              Осведомленность
            </label>
            <span className="text-sm font-medium text-teal-600 dark:text-teal-400">
              {formData.goals.awarenessWeight}%
            </span>
          </div>
          <input
            type="range"
            id="awarenessWeight"
            min="0"
            max="100"
            step="5"
            value={formData.goals.awarenessWeight}
            onChange={(e) => handleGoalsChange('awarenessWeight', e.target.value)}
            className="w-full h-2 bg-teal-200 dark:bg-teal-700 rounded-lg appearance-none cursor-pointer accent-teal-600"
          />
          {errors.goals_awarenessWeight && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.goals_awarenessWeight}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Повышение узнаваемости бренда и продукта среди целевой аудитории
          </p>
        </div>

        {/* Вовлеченность */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <label htmlFor="engagementWeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <ThumbsUp className="w-4 h-4 mr-1 text-teal-500" />
              Вовлеченность
            </label>
            <span className="text-sm font-medium text-teal-600 dark:text-teal-400">
              {formData.goals.engagementWeight}%
            </span>
          </div>
          <input
            type="range"
            id="engagementWeight"
            min="0"
            max="100"
            step="5"
            value={formData.goals.engagementWeight}
            onChange={(e) => handleGoalsChange('engagementWeight', e.target.value)}
            className="w-full h-2 bg-teal-200 dark:bg-teal-700 rounded-lg appearance-none cursor-pointer accent-teal-600"
          />
          {errors.goals_engagementWeight && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.goals_engagementWeight}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Взаимодействие пользователей с контентом (клики, комментарии, лайки и т.д.)
          </p>
        </div>

        {/* Конверсии */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <label htmlFor="conversionWeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <DollarSign className="w-4 h-4 mr-1 text-teal-500" />
              Конверсии
            </label>
            <span className="text-sm font-medium text-teal-600 dark:text-teal-400">
              {formData.goals.conversionWeight}%
            </span>
          </div>
          <input
            type="range"
            id="conversionWeight"
            min="0"
            max="100"
            step="5"
            value={formData.goals.conversionWeight}
            onChange={(e) => handleGoalsChange('conversionWeight', e.target.value)}
            className="w-full h-2 bg-teal-200 dark:bg-teal-700 rounded-lg appearance-none cursor-pointer accent-teal-600"
          />
          {errors.goals_conversionWeight && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.goals_conversionWeight}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Целевые действия (покупки, регистрации, заявки и другие транзакции)
          </p>
        </div>
      </div>

      {/* Общая информация о целях */}
      <div className="mt-4 p-3 bg-teal-100 dark:bg-teal-900/40 rounded-lg border border-teal-200 dark:border-teal-800">
        <div className="flex items-start space-x-3">
          <Target className="w-5 h-5 text-teal-600 dark:text-teal-400 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-teal-700 dark:text-teal-300 mb-1">
              Распределение приоритетов
            </h4>
            <p className="text-xs text-teal-600 dark:text-teal-400">
              Установите вес для каждой цели в зависимости от приоритетов вашего бизнеса. 
              Сумма весов должна составлять 100%. Текущая сумма: <span className={`font-medium ${Math.abs(totalWeight - 100) > 0.1 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>{totalWeight.toFixed(1)}%</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalsSection;